import { Howl } from 'howler'
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react'

type AudioNotificationContextType = {
  playNewMessageSound: () => void
}

const AudioNotificationContext = createContext<
  AudioNotificationContextType | undefined
>(undefined)

type AudioNotificationProviderProps = {
  children: ReactNode
}

export function AudioNotificationProvider({
  children,
}: AudioNotificationProviderProps) {
  const notificationSoundRef = useRef<Howl | null>(null)

  useEffect(() => {
    notificationSoundRef.current = new Howl({
      src: ['/audios/receive-message-notification.mp3'],
      preload: true,
    })

    return () => {
      if (notificationSoundRef.current) {
        notificationSoundRef.current.unload()
        notificationSoundRef.current = null
      }
    }
  }, [])

  const playNewMessageSound = () => {
    if (notificationSoundRef.current) {
      notificationSoundRef.current.stop()
      notificationSoundRef.current.play()
    }
  }

  return (
    <AudioNotificationContext.Provider value={{ playNewMessageSound }}>
      {children}
    </AudioNotificationContext.Provider>
  )
}

export function useAudioNotification() {
  const context = useContext(AudioNotificationContext)
  if (!context) {
    throw new Error(
      'useAudioNotification must be used within AudioNotificationProvider'
    )
  }
  return context
}
